import React, { useState, useEffect } from 'react';
import '../Style.scss';
import { ClientSideTable } from 'Components/Common/ClientSideTable/ClientSideTable';
import UserEditModel from 'Models/SuperUser/UserEditModel';
import { Column, CellInfo } from 'react-table';
import ExpansionPanel from 'react-md/lib/ExpansionPanels';
import { FN_EMPTY_VOID } from 'Util/Helpers/Empty';
import { useSelector, useDispatch } from 'react-redux';
import {
   IdentityActions,
   selectUnlockUserResponse,
   selectMfaResetResponse,
} from 'State/Redux/IdentityRedux';
import { ContactTypes } from 'State/Redux/ContactRedux';
import { selectIsLoading } from 'State/Redux/AsyncRedux';
import FaIcon from 'Components/Common/FaIcon/FaIcon';
import Button from 'react-md/lib/Buttons';
import DialogContainer from 'react-md/lib/Dialogs';
import { EditUser } from '../../EditUser';
import { toast } from 'react-toastify';

interface SearchResultProps {
   users: UserEditModel[];
   triggerSearch: () => void;
}

const SearchResult = ({
   users,
   triggerSearch,
}: Readonly<SearchResultProps>): JSX.Element => {
   const isLoading = useSelector(
      selectIsLoading([ContactTypes.SEARCH_ADMIN_USER_REQUEST])
   );

   const [editDialogVisible, setEditDialogVisible] = useState(false);
   const [editUserId, setEditUserId] = useState();
   const [mfaResetDialogVisible, setMfaResetDialogVisible] = useState(false);
   const [mfaResetUserId, setMfaResetUserId] = useState<string | undefined>();

   const dispatch = useDispatch();
   const userUnlockResponse = useSelector(selectUnlockUserResponse);
   const mfaResetResponse = useSelector(selectMfaResetResponse);

   useEffect(() => {
      if (userUnlockResponse && userUnlockResponse.message) {
         if (userUnlockResponse.success) {
            toast.success(userUnlockResponse.message);
            triggerSearch();
         } else {
            toast.error(userUnlockResponse.message);
         }
         dispatch(IdentityActions.unlockUserResponseReset());
      }
   }, [dispatch, triggerSearch, userUnlockResponse]);

   useEffect(() => {
      if (mfaResetResponse && mfaResetResponse.message) {
         if (mfaResetResponse.success) {
            toast.success(mfaResetResponse.message);
            triggerSearch();
         } else {
            toast.error(mfaResetResponse.message);
         }
         dispatch(IdentityActions.mfaResetResponseReset());
      }
   }, [dispatch, triggerSearch, mfaResetResponse]);

   const handleUnlock = (userId: string): void => {
      dispatch(IdentityActions.postUnlockUserRequest(userId));
   };

   const handleMfaReset = (): void => {
      if (mfaResetUserId) {
         dispatch(IdentityActions.postMfaResetRequest(mfaResetUserId));
      }
      setMfaResetDialogVisible(false);
   };

   const StatusCell = (cell: CellInfo): JSX.Element => {
      return (
         <div className="status-cell">
            {cell.value ? (
               <div className="status-icon">
                  <FaIcon icon="user" className="small-icon red" />
                  <span>Disabled</span>
               </div>
            ) : (
               <div className="status-icon">
                  <FaIcon icon="user" className="small-icon green" />
                  <span>Active</span>
               </div>
            )}
         </div>
      );
   };

   const COLUMN_HEADERS: Column<UserEditModel>[] = [
      {
         Header: 'User Id',
         accessor: 'userName',
      },
      {
         Header: 'Full Name',
         accessor: 'fullName',
      },
      {
         Header: 'Email',
         accessor: 'emailAddress',
      },
      {
         Header: 'Role',
         accessor: 'roleCode',
      },
      {
         Header: 'Status',
         accessor: 'isDisabled',
         Cell: StatusCell,
      },
      {
         Header: 'Locked Out',
         accessor: 'isLockedOut',
         Cell: (cell: CellInfo): JSX.Element => {
            // Defensive: fallback to false if undefined
            const value = typeof cell.value === 'boolean' ? cell.value : false;
            return (
               <div className="status-cell">
                  {value ? (
                     <div className="status-icon">
                        <FaIcon icon="lock" className="small-icon red" />
                        <span>Locked</span>
                     </div>
                  ) : (
                     <div className="status-icon">
                        <FaIcon icon="unlock" className="small-icon green" />
                        <span>Unlocked</span>
                     </div>
                  )}
               </div>
            );
         },
      },
      {
         Header: 'MFA',
         accessor: 'mfaDetails',
         sortable: false,
         Cell: (cell: CellInfo): JSX.Element => {
            const isEnrolled = !!cell.value?.totpEnrolledDate;
            return (
               <div className="status-cell">
                  {isEnrolled ? (
                     <div className="status-icon">
                        <FaIcon
                           icon="user-shield"
                           className="small-icon green"
                        />
                        <span>Enrolled</span>
                     </div>
                  ) : (
                     <div className="status-icon">
                        <FaIcon icon="user-shield" className="small-icon red" />
                        <span>Not Enrolled</span>
                     </div>
                  )}
               </div>
            );
         },
      },
      {
         Header: '',
         Cell: (cell: CellInfo): JSX.Element => {
            const original =
               (cell.row && (cell.row._original || cell.row.original)) || {};
            const isLockedOut =
               typeof original.isLockedOut === 'boolean'
                  ? original.isLockedOut
                  : false;
            const isMfaEnrolled = !!original.mfaDetails?.totpEnrolledDate;
            const userId = original.userId;
            return (
               <>
                  <Button
                     icon
                     className="table-row-button"
                     iconEl={<FaIcon icon="edit" className="small-icon" />}
                     onClick={(): void => {
                        setEditUserId(cell.value);
                        setEditDialogVisible(true);
                     }}
                  />
                  {isMfaEnrolled && userId && (
                     <Button
                        icon
                        className="table-row-button"
                        iconEl={
                           <FaIcon
                              icon="user-shield"
                              className="small-icon orange"
                           />
                        }
                        onClick={(): void => {
                           setMfaResetUserId(userId);
                           setMfaResetDialogVisible(true);
                        }}
                        title="Reset MFA"
                        style={{ marginLeft: 8 }}
                     />
                  )}
                  {isLockedOut && userId && (
                     <Button
                        icon
                        className="table-row-button"
                        iconEl={
                           <FaIcon icon="unlock" className="small-icon red" />
                        }
                        onClick={(): void => {
                           handleUnlock(userId);
                        }}
                        title="Unlock User"
                        style={{ marginLeft: 8 }}
                     />
                  )}
               </>
            );
         },
         accessor: 'userId',
         sortable: false,
      },
   ];

   const renderEditDialog = (): JSX.Element => {
      return (
         <DialogContainer
            id="dialog-container-user-edit"
            className="user-edit-dialog"
            visible={editDialogVisible}
            focusOnMount={false}
            containFocus={false}
            closeOnEsc
            paddedContent={false}
         >
            <EditUser
               editUserId={editUserId}
               onCancel={(): void => setEditDialogVisible(false)}
               onSave={(): void => {
                  setEditDialogVisible(false);
                  triggerSearch();
               }}
            />
         </DialogContainer>
      );
   };

   const renderMfaResetDialog = (): JSX.Element => {
      return (
         <DialogContainer
            id="dialog-container-mfa-reset-confirm"
            visible={mfaResetDialogVisible}
            title="Reset MFA"
            width={500}
            portal
            onHide={FN_EMPTY_VOID}
            contentStyle={{ padding: '0 24px 24px' }}
            footerStyle={{ padding: '0 24px 24px 24px' }}
            actions={[
               <Button
                  key="cancel"
                  flat
                  style={{ color: '#039be5', padding: '0 16px' }}
                  onClick={(): void => setMfaResetDialogVisible(false)}
               >
                  Cancel
               </Button>,
               <Button
                  key="confirm"
                  flat
                  style={{ color: '#f57c00', padding: '0 16px' }}
                  onClick={handleMfaReset}
               >
                  Confirm
               </Button>,
            ]}
         >
            <span>
               Are you sure you want to reset MFA for this user? This will
               remove all enrolled methods and require re-enrollment at next
               login.
            </span>
         </DialogContainer>
      );
   };

   return (
      <ExpansionPanel
         label={`Results (${users.length})`}
         footer={null}
         expanded
         expanderIcon={<></>}
         onExpandToggle={FN_EMPTY_VOID}
         className="user-search-result"
      >
         <ClientSideTable
            data={users}
            defaultPageSize={10}
            columns={COLUMN_HEADERS}
            loading={isLoading}
         />
         {renderEditDialog()}
         {renderMfaResetDialog()}
      </ExpansionPanel>
   );
};

export default SearchResult;
